import type { LiveServerMessage, LiveStatus, LiveValue } from '@atnt/shared';
import { api, API_BASE } from '@/shared/api/client';

/**
 * One live-data connection per browser tab (docs/design/02-constructor.md §11.3, packages/shared/src/schemas/live.ts).
 *
 *  - Subscriptions are ref-counted per (tenant, sensor): every `retain()` returns its own release function; the
 *    socket only (un)subscribes ids whose count crosses 0 ↔ 1, batched per microtask (StrictMode-safe).
 *  - First values come from REST (`GET /tenants/:t/live/latest?ids=`) so a page renders values before the socket
 *    is up; then `snapshot` / `update` messages keep them current.
 *  - Reconnects with exponential backoff (1 → 30 s ± 30 % jitter), re-subscribes everything, polls REST while
 *    disconnected, and closes the socket after a short idle period without subscriptions.
 *  - Unknown message types are ignored by the value cache but forwarded to `onMessage` listeners (alarms etc.).
 *  - Keeps a small in-memory history per sensor (values seen since the page opened) for live trend charts.
 */

export type ConnectionState = 'idle' | 'connecting' | 'open' | 'reconnecting';

export interface LiveReading {
  value: number | null;
  status: LiveStatus;
  /** ISO time of the measurement */
  ts: string | null;
  /** when the browser received it (ms) */
  receivedAt: number;
}

/** [measurement time ms, value] */
export type HistoryPoint = readonly [number, number | null];

export type LiveMessage = { type: string } & Record<string, unknown>;

const HISTORY_LIMIT = 2000;
const BACKOFF_MIN = 1000;
const BACKOFF_MAX = 30_000;
const POLL_WHILE_DOWN_MS = 15_000;
const IDLE_CLOSE_MS = 15_000;
const PING_EVERY_MS = 25_000;
const PONG_TIMEOUT_MS = 10_000;
const REST_CHUNK = 100;
/** distinct id-sets whose snapshots are cached (oldest evicted first) */
const SNAPSHOT_CACHE_LIMIT = 500;

const EMPTY_HISTORY: readonly HistoryPoint[] = Object.freeze([]);

export class LiveStore {
  #ws: WebSocket | null = null;
  #state: ConnectionState = 'idle';
  /** tenantId → sensorId → ref count */
  #refs = new Map<string, Map<string, number>>();
  #readings = new Map<string, LiveReading>();
  #versions = new Map<string, number>();
  #history = new Map<string, readonly HistoryPoint[]>();
  #listeners = new Map<string, Set<() => void>>();
  #stateListeners = new Set<() => void>();
  #messageListeners = new Set<(msg: LiveMessage) => void>();
  #multi = new Map<string, { versions: number[]; list: readonly (LiveReading | undefined)[] }>();
  #multiHistory = new Map<string, { versions: number[]; list: readonly (readonly HistoryPoint[])[] }>();
  #pendingSub = new Map<string, Set<string>>();
  #pendingUnsub = new Map<string, Set<string>>();
  #flushQueued = false;
  #attempt = 0;
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  #pollTimer: ReturnType<typeof setInterval> | null = null;
  #idleTimer: ReturnType<typeof setTimeout> | null = null;
  #pingTimer: ReturnType<typeof setInterval> | null = null;
  #pongTimer: ReturnType<typeof setTimeout> | null = null;
  #visibilityHooked = false;

  /* ───────────── public API ───────────── */

  /** Subscribe to sensors of a tenant; returns the release function. */
  retain(tenantId: string, sensorIds: readonly string[]): () => void {
    const ids = [...new Set(sensorIds.filter(Boolean))];
    if (!tenantId || !ids.length) return () => undefined;
    let counts = this.#refs.get(tenantId);
    if (!counts) this.#refs.set(tenantId, (counts = new Map()));
    for (const id of ids) {
      const n = counts.get(id) ?? 0;
      counts.set(id, n + 1);
      if (n === 0) {
        const unsub = this.#pendingUnsub.get(tenantId);
        if (unsub?.has(id)) unsub.delete(id);
        else this.#pending(this.#pendingSub, tenantId).add(id);
      }
    }
    this.#queueFlush();
    this.#ensureConnected();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      const map = this.#refs.get(tenantId);
      if (!map) return;
      for (const id of ids) {
        const n = (map.get(id) ?? 0) - 1;
        if (n > 0) {
          map.set(id, n);
          continue;
        }
        map.delete(id);
        const sub = this.#pendingSub.get(tenantId);
        if (sub?.has(id)) sub.delete(id);
        else this.#pending(this.#pendingUnsub, tenantId).add(id);
      }
      if (!map.size) this.#refs.delete(tenantId);
      this.#queueFlush();
      this.#scheduleIdleClose();
    };
  }

  get(sensorId: string): LiveReading | undefined {
    return this.#readings.get(sensorId);
  }

  /** Stable snapshot (same array while none of the readings changed) — for `useSyncExternalStore`. */
  getMany(sensorIds: readonly string[]): readonly (LiveReading | undefined)[] {
    const key = sensorIds.join('|');
    const versions = sensorIds.map((id) => this.#versions.get(id) ?? 0);
    const cached = this.#multi.get(key);
    if (cached && cached.versions.length === versions.length && cached.versions.every((v, i) => v === versions[i])) return cached.list;
    const list = Object.freeze(sensorIds.map((id) => this.#readings.get(id)));
    if (!cached && this.#multi.size >= SNAPSHOT_CACHE_LIMIT) this.#multi.delete(this.#multi.keys().next().value!);
    this.#multi.set(key, { versions, list });
    return list;
  }

  getHistory(sensorId: string): readonly HistoryPoint[] {
    return this.#history.get(sensorId) ?? EMPTY_HISTORY;
  }

  getHistoryMany(sensorIds: readonly string[]): readonly (readonly HistoryPoint[])[] {
    const key = sensorIds.join('|');
    const versions = sensorIds.map((id) => this.#versions.get(id) ?? 0);
    const cached = this.#multiHistory.get(key);
    if (cached && cached.versions.length === versions.length && cached.versions.every((v, i) => v === versions[i])) return cached.list;
    const list = Object.freeze(sensorIds.map((id) => this.getHistory(id)));
    if (!cached && this.#multiHistory.size >= SNAPSHOT_CACHE_LIMIT) this.#multiHistory.delete(this.#multiHistory.keys().next().value!);
    this.#multiHistory.set(key, { versions, list });
    return list;
  }

  /** Listen to changes of one sensor. */
  listen(sensorId: string, listener: () => void): () => void {
    let set = this.#listeners.get(sensorId);
    if (!set) this.#listeners.set(sensorId, (set = new Set()));
    set.add(listener);
    return () => {
      set.delete(listener);
      if (!set.size) this.#listeners.delete(sensorId);
    };
  }

  listenMany(sensorIds: readonly string[], listener: () => void): () => void {
    const offs = [...new Set(sensorIds)].map((id) => this.listen(id, listener));
    return () => offs.forEach((off) => off());
  }

  get connection(): ConnectionState {
    return this.#state;
  }

  onConnectionChange(listener: () => void): () => void {
    this.#stateListeners.add(listener);
    return () => this.#stateListeners.delete(listener);
  }

  /** Every server message (including types this store does not know, e.g. future `{ type: 'alarm' }`). */
  onMessage(listener: (msg: LiveMessage) => void): () => void {
    this.#messageListeners.add(listener);
    this.#ensureConnected();
    return () => this.#messageListeners.delete(listener);
  }

  /** Feed values from another source (tests, REST refresh). */
  apply(values: readonly LiveValue[]) {
    const now = Date.now();
    for (const v of values) {
      if (!v || typeof v.indicatorId !== 'string') continue;
      const prev = this.#readings.get(v.indicatorId);
      if (prev && prev.ts === v.ts && prev.value === v.value && prev.status === v.status) continue;
      if (prev?.ts && v.ts && Date.parse(v.ts) < Date.parse(prev.ts)) continue; // older than what we have
      this.#readings.set(v.indicatorId, { value: v.value, status: v.status, ts: v.ts, receivedAt: now });
      const t = v.ts ? Date.parse(v.ts) : now;
      const hist = this.#history.get(v.indicatorId) ?? EMPTY_HISTORY;
      const lastT = hist.length ? hist[hist.length - 1]![0] : -Infinity;
      if (Number.isFinite(t) && t > lastT) {
        const next = hist.length >= HISTORY_LIMIT ? hist.slice(hist.length - HISTORY_LIMIT + 1) : hist.slice();
        next.push([t, v.value]);
        this.#history.set(v.indicatorId, Object.freeze(next));
      }
      this.#versions.set(v.indicatorId, (this.#versions.get(v.indicatorId) ?? 0) + 1);
      this.#listeners.get(v.indicatorId)?.forEach((l) => l());
    }
  }

  /* ───────────── subscriptions ───────────── */

  #pending(map: Map<string, Set<string>>, tenantId: string) {
    let set = map.get(tenantId);
    if (!set) map.set(tenantId, (set = new Set()));
    return set;
  }

  #queueFlush() {
    if (this.#flushQueued) return;
    this.#flushQueued = true;
    queueMicrotask(() => {
      this.#flushQueued = false;
      this.#flush();
    });
  }

  #flush() {
    const open = this.#ws?.readyState === WebSocket.OPEN;
    for (const [tenantId, ids] of this.#pendingSub) {
      if (!ids.size) continue;
      const list = [...ids];
      if (open) this.#send({ type: 'subscribe', tenantId, indicatorIds: list });
      void this.#fetchLatest(tenantId, list.filter((id) => !this.#readings.has(id)));
    }
    for (const [tenantId, ids] of this.#pendingUnsub) {
      if (ids.size && open) this.#send({ type: 'unsubscribe', tenantId, indicatorIds: [...ids] });
    }
    this.#pendingSub.clear();
    this.#pendingUnsub.clear();
  }

  async #fetchLatest(tenantId: string, ids: string[]) {
    for (let i = 0; i < ids.length; i += REST_CHUNK) {
      const chunk = ids.slice(i, i + REST_CHUNK);
      try {
        const values = await api.get<LiveValue[]>(`/tenants/${encodeURIComponent(tenantId)}/live/latest`, { query: { ids: chunk.join(',') } });
        this.apply(values);
      } catch {
        /* the socket will deliver values once connected */
      }
    }
  }

  #retainedByTenant(): Map<string, string[]> {
    const out = new Map<string, string[]>();
    for (const [tenantId, counts] of this.#refs) if (counts.size) out.set(tenantId, [...counts.keys()]);
    return out;
  }

  /* ───────────── socket lifecycle ───────────── */

  #setState(state: ConnectionState) {
    if (this.#state === state) return;
    this.#state = state;
    this.#stateListeners.forEach((l) => l());
  }

  #hasWork() {
    return this.#refs.size > 0 || this.#messageListeners.size > 0;
  }

  #ensureConnected() {
    if (typeof window === 'undefined' || typeof WebSocket === 'undefined') return;
    if (this.#idleTimer) {
      clearTimeout(this.#idleTimer);
      this.#idleTimer = null;
    }
    this.#hookVisibility();
    if (this.#ws || this.#reconnectTimer) return;
    this.#connect();
  }

  #connect() {
    this.#reconnectTimer = null;
    if (!this.#hasWork()) return this.#setState('idle');
    const url = new URL(`${API_BASE}/live`, window.location.href);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      return this.#scheduleReconnect();
    }
    this.#ws = ws;
    this.#setState(this.#attempt > 0 ? 'reconnecting' : 'connecting');
    ws.onopen = () => {
      if (this.#ws !== ws) return;
      this.#attempt = 0;
      this.#setState('open');
      this.#stopPolling();
      // (re)subscribe everything we hold; snapshots refresh the cache
      for (const [tenantId, ids] of this.#retainedByTenant()) this.#send({ type: 'subscribe', tenantId, indicatorIds: ids });
      this.#pendingSub.clear();
      this.#pendingUnsub.clear();
      this.#startPing();
    };
    ws.onmessage = (ev) => {
      if (this.#ws !== ws || typeof ev.data !== 'string') return;
      let msg: LiveMessage;
      try {
        msg = JSON.parse(ev.data) as LiveMessage;
      } catch {
        return;
      }
      if (!msg || typeof msg.type !== 'string') return;
      this.#handle(msg);
    };
    ws.onclose = () => {
      if (this.#ws !== ws) return;
      this.#ws = null;
      this.#stopPing();
      if (!this.#hasWork()) return this.#setState('idle');
      this.#scheduleReconnect();
    };
    ws.onerror = () => {
      /* followed by close */
    };
  }

  #handle(msg: LiveMessage) {
    const known = msg as unknown as LiveServerMessage;
    switch (known.type) {
      case 'snapshot':
      case 'update':
        if (Array.isArray(known.values)) this.apply(known.values);
        break;
      case 'pong':
        if (this.#pongTimer) clearTimeout(this.#pongTimer);
        this.#pongTimer = null;
        break;
      case 'error':
        if (import.meta.env.DEV) console.warn('[live]', known.code, known.message);
        break;
      default:
        break; // unknown types are ignored here (listeners below may use them)
    }
    this.#messageListeners.forEach((l) => l(msg));
  }

  #send(msg: object) {
    if (this.#ws?.readyState === WebSocket.OPEN) this.#ws.send(JSON.stringify(msg));
  }

  #scheduleReconnect() {
    if (this.#reconnectTimer || !this.#hasWork()) return;
    this.#setState('reconnecting');
    this.#startPolling();
    const base = Math.min(BACKOFF_MAX, BACKOFF_MIN * 2 ** this.#attempt);
    const delay = base * (0.7 + Math.random() * 0.6);
    this.#attempt += 1;
    this.#reconnectTimer = setTimeout(() => this.#connect(), delay);
  }

  #startPing() {
    this.#stopPing();
    this.#pingTimer = setInterval(() => {
      if (this.#ws?.readyState !== WebSocket.OPEN) return;
      this.#send({ type: 'ping' });
      if (this.#pongTimer) clearTimeout(this.#pongTimer);
      this.#pongTimer = setTimeout(() => this.#ws?.close(), PONG_TIMEOUT_MS);
    }, PING_EVERY_MS);
  }

  #stopPing() {
    if (this.#pingTimer) clearInterval(this.#pingTimer);
    if (this.#pongTimer) clearTimeout(this.#pongTimer);
    this.#pingTimer = null;
    this.#pongTimer = null;
  }

  /** While the socket is down, keep values fresh over HTTP. */
  #startPolling() {
    if (this.#pollTimer) return;
    this.#pollTimer = setInterval(() => {
      for (const [tenantId, ids] of this.#retainedByTenant()) void this.#fetchLatest(tenantId, ids);
    }, POLL_WHILE_DOWN_MS);
  }

  #stopPolling() {
    if (this.#pollTimer) clearInterval(this.#pollTimer);
    this.#pollTimer = null;
  }

  #scheduleIdleClose() {
    if (this.#hasWork() || this.#idleTimer) return;
    this.#idleTimer = setTimeout(() => {
      this.#idleTimer = null;
      if (this.#hasWork()) return;
      if (this.#reconnectTimer) clearTimeout(this.#reconnectTimer);
      this.#reconnectTimer = null;
      this.#stopPolling();
      this.#stopPing();
      const ws = this.#ws;
      this.#ws = null;
      ws?.close();
      this.#attempt = 0;
      this.#setState('idle');
    }, IDLE_CLOSE_MS);
  }

  #hookVisibility() {
    if (this.#visibilityHooked || typeof document === 'undefined') return;
    this.#visibilityHooked = true;
    const wake = () => {
      if (document.visibilityState !== 'visible' || !this.#hasWork() || this.#ws) return;
      if (this.#reconnectTimer) clearTimeout(this.#reconnectTimer);
      this.#reconnectTimer = null;
      this.#connect();
    };
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('online', wake);
  }
}

/** The tab-wide store (one WebSocket per tab). */
export const liveStore = new LiveStore();
